import { Center, Flex } from "styled-system/jsx";
import { Button, Heading, Icon, Text } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import { HeavyRain, Refresh, RefreshDouble } from "iconoir-react";
import { useAuth0 } from "@auth0/auth0-react";
import { useHelpScout } from "shared/hooks";
import { clearAllClientStorage } from "shared/utils/resetAuthSession";

type ErrorBoundaryPageProps = {
  /**
   * Auth0 "reset and sign out" only exists under `/app`. Public payer routes
   * (`/invoice`, `/order`) have no Auth0Provider, so keep this off there.
   */
  showResetAndSignOut?: boolean;
};

const ResetAndSignOutButton = () => {
  const { t } = useTranslation();
  const { logout } = useAuth0();

  /**
   * Superset of a plain logout — see resetAuthSession.ts for why a plain
   * logout can fail to unblock a user (it leaves Auth0's cached tokens and
   * our own storage bookkeeping in place) and why this is offered instead
   * of a separate "just log out" button.
   */
  const handleResetAndSignOut = () => {
    clearAllClientStorage();
    logout({
      logoutParams: { returnTo: window.location.origin },
    });
  };

  return (
    <Button
      visual={"outline"}
      mode={"light"}
      size={"md"}
      Icon={RefreshDouble}
      onClick={handleResetAndSignOut}
    >
      {t("core:ERROR_BOUNDARY.RESET_AND_SIGN_OUT")}
    </Button>
  );
};

export const ErrorBoundaryPage = ({
  showResetAndSignOut = true,
}: ErrorBoundaryPageProps = {}) => {
  const { t } = useTranslation();
  const { handleBeaconClick } = useHelpScout();

  return (
    <Center
      bg={"bg.wa.lightest"}
      w={"full"}
      h={"full"}
      py={"1000"}
      px={{
        base: "300",
        md: "1000",
      }}
    >
      <Center maxW={"600px"} gap={"400"} flexDir={"column"}>
        <Icon
          Element={HeavyRain}
          w={"60px"}
          h={"60px"}
          color={"icon.accent.blue.mid"}
        />
        <Heading size={"h4"} textAlign="center">
          {t("core:ERROR_BOUNDARY.TITLE")}
        </Heading>
        <Trans
          i18nKey={"core:ERROR_BOUNDARY.DESCRIPTION"}
          components={{
            Text: (
              <Text
                visual={"bodyMedium"}
                color={"text.mid"}
                textAlign={"center"}
              />
            ),
            Button: (
              <Button
                visual="unstyled"
                textDecoration={"underline"}
                display="inline"
                onClick={() => handleBeaconClick({})}
              />
            ),
          }}
        />
        <Flex gap={"200"} flexWrap={"wrap"} justifyContent={"center"}>
          <Button
            visual={"primary"}
            mode={"light"}
            size={"lg"}
            Icon={Refresh}
            onClick={() => window.location.reload()}
          >
            {t("core:ERROR_BOUNDARY.CTA_BUTTON_COPY")}
          </Button>
        </Flex>
        {showResetAndSignOut ? <ResetAndSignOutButton /> : null}
      </Center>
    </Center>
  );
};
